.field {
  --field-bg: #101710;
  --field-ink: #f0f0e5;
  --field-lime: #c2f23d;
  --field-moss: #293529;
  --field-paper: #e9e7d9;
  --field-muted: #9aa397;
  color: var(--field-ink);
  background: var(--field-bg);
  font-family: "Outfit", sans-serif;
}

.field-nav {
  position: absolute;
  z-index: 20;
  top: 0;
  right: 0;
  left: 0;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  min-height: 88px;
  padding: 0 clamp(22px, 4vw, 72px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.17);
}

.field-logo,
.field-nav nav,
.field-nav__cta,
.field-hero__content > p:first-child,
.field-hero__status,
.field-proof,
.field-platform__intro > p:first-child,
.field-process__heading > p,
.field-process article > span,
.field-calculator__copy > p:first-child,
.field-footer {
  font-family: "Roboto Mono", monospace;
}

.field-logo {
  color: var(--field-lime);
  font-size: 18px;
  font-weight: 600;
}

.field-nav nav {
  display: flex;
  gap: 32px;
  font-size: 11px;
  text-transform: uppercase;
}

.field-nav nav a,
.field-nav__cta {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
}

.field-nav__cta {
  justify-self: end;
  padding: 0 17px;
  color: var(--field-bg);
  background: var(--field-lime);
  font-size: 11px;
  text-transform: uppercase;
}

.field-hero {
  position: relative;
  display: grid;
  place-items: center;
  min-height: max(820px, 100svh);
  overflow: hidden;
  text-align: center;
}

.field-hero__bg,
.field-hero__overlay {
  position: absolute;
  inset: 0;
}

.field-hero__bg img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.field-hero__overlay {
  background:
    linear-gradient(180deg, rgba(8, 13, 8, 0.55), rgba(8, 13, 8, 0.35) 45%, rgba(8, 13, 8, 0.8)),
    radial-gradient(circle at center, transparent 12%, rgba(8, 13, 8, 0.28));
}

.field-hero::after {
  position: absolute;
  z-index: 1;
  inset: -25% 0 auto;
  height: 24%;
  pointer-events: none;
  content: "";
  background: linear-gradient(180deg, transparent, rgba(194, 242, 61, 0.16), transparent);
  mix-blend-mode: screen;
  animation: field-scan 7s linear infinite;
}

@keyframes field-scan {
  to {
    transform: translateY(620%);
  }
}

.field-hero__content {
  position: relative;
  z-index: 2;
  width: min(92%, 1230px);
  padding-top: 80px;
}

.field-hero__content > p:first-child {
  margin: 0 0 26px;
  color: var(--field-lime);
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.field-hero h1 {
  max-width: 1200px;
  margin: 0 auto;
  font-size: clamp(4rem, 9vw, 10.5rem);
  font-weight: 700;
  line-height: 0.83;
  letter-spacing: -0.075em;
}

.field-hero__content > p:not(:first-child) {
  max-width: 650px;
  margin: 34px auto 0;
  color: #d6d9ce;
  font-size: clamp(1.05rem, 1.5vw, 1.3rem);
  line-height: 1.55;
}

.field-hero__actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 28px;
  margin-top: 38px;
  font-size: 13px;
}

.field-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 54px;
  padding: 0 22px;
  color: var(--field-bg);
  background: var(--field-lime);
  font-family: "Roboto Mono", monospace;
  font-size: 11px;
  text-transform: uppercase;
  transition: transform 180ms ease, background 180ms ease;
}

.field-button:hover {
  background: #d4ff5d;
  transform: translateY(-2px);
}

.field-button svg {
  width: 16px;
}

.field-hero__status {
  position: absolute;
  z-index: 3;
  right: 32px;
  bottom: 28px;
  display: grid;
  gap: 5px;
  padding: 14px 16px;
  color: var(--field-bg);
  background: var(--field-lime);
  font-size: 10px;
  text-align: left;
  text-transform: uppercase;
}

.field-hero__status b {
  font-weight: 600;
}

.field-hero__telemetry {
  position: absolute;
  z-index: 4;
  bottom: 28px;
  left: 32px;
  display: grid;
  grid-template-columns: repeat(3, minmax(120px, 1fr));
  color: #eef0e5;
  background: rgba(10, 15, 10, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.24);
  backdrop-filter: blur(14px);
}

.field-hero__telemetry > div {
  display: grid;
  gap: 6px;
  min-width: 145px;
  padding: 14px 16px;
  border-right: 1px solid rgba(255, 255, 255, 0.18);
}

.field-hero__telemetry > div:last-child {
  border-right: 0;
}

.field-hero__telemetry span,
.field-hero__telemetry i {
  font-family: "Roboto Mono", monospace;
  font-size: 8px;
  font-style: normal;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.field-hero__telemetry span {
  color: #9aa397;
}

.field-hero__telemetry strong {
  font-size: 20px;
  font-weight: 500;
}

.field-hero__telemetry i {
  color: var(--field-lime);
}

.field-proof {
  display: grid;
  grid-template-columns: repeat(3, 1fr) 1.25fr;
  border-top: 1px solid #334033;
  border-bottom: 1px solid #334033;
}

.field-proof > div,
.field-proof > p {
  min-height: 155px;
  padding: 28px clamp(20px, 3vw, 48px);
  border-right: 1px solid #334033;
}

.field-ticker {
  overflow: hidden;
  color: var(--field-bg);
  background: var(--field-lime);
}

.field-ticker > div {
  display: flex;
  gap: 28px;
  align-items: center;
  width: max-content;
  min-height: 72px;
  padding-right: 28px;
  font-family: "Roboto Mono", monospace;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.14em;
  animation: field-ticker 22s linear infinite;
}

.field-ticker i {
  font-size: 22px;
  font-style: normal;
}

@keyframes field-ticker {
  to {
    transform: translateX(-50%);
  }
}

.field-proof > div {
  display: grid;
  grid-template-columns: auto 1fr;
  align-content: center;
  gap: 4px 14px;
}

.field-proof svg {
  grid-row: span 2;
  align-self: center;
  width: 20px;
  color: var(--field-lime);
}

.field-proof span {
  color: var(--field-muted);
  font-size: 10px;
  text-transform: uppercase;
}

.field-proof strong {
  font-size: clamp(1.6rem, 2.6vw, 3rem);
  font-weight: 500;
  letter-spacing: -0.04em;
}

.field-proof > p {
  display: grid;
  align-content: center;
  margin: 0;
  color: var(--field-muted);
  font-size: 10px;
  line-height: 1.6;
  text-transform: uppercase;
  border-right: 0;
}

.field-platform {
  display: grid;
  grid-template-columns: minmax(320px, 0.42fr) minmax(0, 0.58fr);
  gap: clamp(50px, 8vw, 130px);
  padding: clamp(100px, 14vw, 210px) clamp(22px, 5vw, 84px);
  color: #182018;
  background: var(--field-paper);
}

.field-platform__intro {
  align-self: center;
}

.field-platform__intro > p:first-child,
.field-process__heading > p,
.field-calculator__copy > p:first-child {
  margin: 0 0 24px;
  color: #657065;
  font-size: 10px;
  text-transform: uppercase;
}

.field-platform__intro h2,
.field-process__heading h2,
.field-calculator__copy h2,
.field-contact h2 {
  margin: 0;
  font-size: clamp(3.4rem, 6vw, 7.2rem);
  font-weight: 700;
  line-height: 0.88;
  letter-spacing: -0.07em;
}

.field-platform__intro > p:last-child,
.field-calculator__copy > p:last-child {
  max-width: 50ch;
  margin: 34px 0 0;
  color: #566056;
  font-size: 18px;
  line-height: 1.65;
}

.field-map {
  position: relative;
  min-height: 760px;
  overflow: hidden;
  background: #293529;
}

.field-map > img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: saturate(0.8) contrast(1.12);
  transition: filter 500ms ease;
}

.field-map--health > img {
  filter: saturate(1.18) contrast(1.05) hue-rotate(-6deg);
}

.field-map--moisture > img {
  filter: saturate(0.74) contrast(1.12) hue-rotate(28deg);
}

.field-map--route > img {
  filter: grayscale(0.4) contrast(1.2);
}

.field-map::after {
  position: absolute;
  inset: 0;
  pointer-events: none;
  content: "";
  background:
    linear-gradient(90deg, transparent 49.8%, rgba(194, 242, 61, 0.25) 50%, transparent 50.2%),
    linear-gradient(transparent 49.8%, rgba(194, 242, 61, 0.25) 50%, transparent 50.2%);
  background-size: 80px 80px;
  mix-blend-mode: screen;
  opacity: 0.45;
}

.field-map::before {
  position: absolute;
  z-index: 2;
  top: 0;
  bottom: 0;
  left: -20%;
  width: 12%;
  pointer-events: none;
  content: "";
  background: linear-gradient(90deg, transparent, rgba(194, 242, 61, 0.28), transparent);
  transform: skewX(-12deg);
  animation: field-map-sweep 5.5s ease-in-out infinite;
}

@keyframes field-map-sweep {
  55%,
  100% {
    left: 115%;
  }
}

.field-map__toolbar {
  position: absolute;
  z-index: 4;
  top: 18px;
  left: 18px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-width: calc(100% - 36px);
}

.field-map__toolbar button {
  min-height: 42px;
  padding: 0 14px;
  color: #eef0e5;
  background: rgba(16, 23, 16, 0.86);
  border: 1px solid rgba(255, 255, 255, 0.25);
  font-family: "Roboto Mono", monospace;
  font-size: 10px;
  text-transform: uppercase;
  backdrop-filter: blur(10px);
}

.field-map__toolbar button[aria-selected="true"] {
  color: var(--field-bg);
  background: var(--field-lime);
  border-color: var(--field-lime);
}

.field-map__legend {
  position: absolute;
  z-index: 4;
  right: 18px;
  bottom: 18px;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 12px;
  width: min(420px, calc(100% - 36px));
  padding: 18px;
  color: var(--field-ink);
  background: rgba(16, 23, 16, 0.92);
  backdrop-filter: blur(12px);
}

.field-map__legend svg {
  width: 20px;
  color: var(--field-lime);
}

.field-map__legend b {
  font-size: 14px;
}

.field-map__legend p {
  margin: 6px 0 0;
  color: var(--field-muted);
  font-size: 12px;
  line-height: 1.5;
}

.field-map__pin {
  position: absolute;
  z-index: 3;
  width: 22px;
  height: 22px;
  background: var(--field-lime);
  border: 6px solid var(--field-bg);
  border-radius: 50%;
  box-shadow: 0 0 0 2px var(--field-lime);
  animation: field-pin 2.4s ease-out infinite;
}

@keyframes field-pin {
  70%,
  100% {
    box-shadow: 0 0 0 2px var(--field-lime), 0 0 0 22px rgba(194, 242, 61, 0);
  }
  0% {
    box-shadow: 0 0 0 2px var(--field-lime), 0 0 0 0 rgba(194, 242, 61, 0.42);
  }
}

.field-map__pin--one {
  top: 34%;
  left: 26%;
}

.field-map__pin--two {
  top: 51%;
  left: 64%;
}

.field-map__pin--three {
  top: 68%;
  left: 44%;
}

.field-process {
  display: block;
  padding-bottom: 18vh;
  color: var(--field-ink);
  background: var(--field-bg);
}

.field-process__heading {
  display: grid;
  grid-template-columns: 0.33fr 0.67fr;
  gap: 50px;
  padding: clamp(100px, 14vw, 210px) clamp(22px, 5vw, 84px) 100px;
}

.field-process__heading h2 {
  max-width: 1050px;
}

.field-process article {
  position: sticky;
  top: 88px;
  display: grid;
  grid-template-columns: 72px 100px minmax(230px, 0.7fr) minmax(300px, 1fr);
  gap: 30px;
  align-items: center;
  min-height: 66vh;
  margin: 0 clamp(22px, 5vw, 84px);
  padding: clamp(28px, 5vw, 72px);
  background: #121c13;
  border: 1px solid #405040;
  box-shadow: 0 -24px 70px rgba(3, 8, 3, 0.28);
}

.field-process article:nth-of-type(2) {
  top: 106px;
  background: #17251a;
}

.field-process article:nth-of-type(3) {
  top: 124px;
  color: #182018;
  background: #d5dcc4;
}

.field-process article:nth-of-type(4) {
  top: 142px;
  color: #182018;
  background: var(--field-lime);
}

.field-process article > span {
  color: var(--field-muted);
  font-size: 10px;
}

.field-process article svg {
  display: block;
  width: 58px;
  height: 58px;
  margin: 0;
  color: var(--field-lime);
}

.field-process article:nth-of-type(3) svg,
.field-process article:nth-of-type(4) svg {
  color: #26351f;
}

.field-process article h3 {
  margin: 0;
  font-size: clamp(2.2rem, 4vw, 5rem);
  font-weight: 600;
  line-height: 0.92;
  letter-spacing: -0.055em;
}

.field-process article p {
  margin: 0;
  color: var(--field-muted);
  font-size: 17px;
  line-height: 1.7;
}

.field-process article:nth-of-type(3) p,
.field-process article:nth-of-type(4) p {
  color: #4e5b49;
}

.field-calculator {
  display: grid;
  grid-template-columns: 0.85fr 1.15fr;
  gap: clamp(60px, 10vw, 170px);
  padding: clamp(100px, 14vw, 210px) clamp(22px, 5vw, 84px);
  color: #182018;
  background: var(--field-paper);
}

.field-calculator__controls {
  display: grid;
  gap: 28px;
}

.field-calculator__controls label {
  display: grid;
  gap: 16px;
}

.field-calculator__controls label > span {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  font-weight: 600;
}

.field-calculator__controls output {
  font-family: "Roboto Mono", monospace;
}

.field-calculator input[type="range"] {
  width: 100%;
  accent-color: #3c5b22;
}

.field-calculator__results {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  border: 1px solid #9fa596;
}

.field-calculator__results > div {
  display: grid;
  align-content: space-between;
  min-height: 180px;
  padding: 22px;
  border-right: 1px solid #9fa596;
}

.field-calculator__results > div:last-child {
  border-right: 0;
}

.field-calculator__results span {
  color: #5e685b;
  font-size: 11px;
  line-height: 1.4;
}

.field-calculator__results strong {
  font-size: clamp(1.6rem, 3vw, 3.4rem);
  font-weight: 600;
  letter-spacing: -0.05em;
}

.field-calculator__controls small {
  color: #6b7467;
}

.field-calculator .field-button {
  width: max-content;
  color: var(--field-ink);
  background: var(--field-bg);
}

.field-contact {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(60px, 10vw, 170px);
  padding: clamp(100px, 14vw, 210px) clamp(22px, 5vw, 84px);
}

.field-contact > div > p {
  margin: 0 0 24px;
  color: var(--field-lime);
  font-family: "Roboto Mono", monospace;
  font-size: 10px;
  text-transform: uppercase;
}

.field-contact .demo-form__submit {
  color: var(--field-bg);
  background: var(--field-lime);
}

.field-contact .demo-form input {
  border-color: #657065;
}

.field-footer {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  padding: 48px clamp(22px, 5vw, 84px) 100px;
  border-top: 1px solid #334033;
  color: var(--field-muted);
  font-size: 10px;
  line-height: 1.7;
  text-transform: uppercase;
}

.field-footer div:last-child {
  text-align: right;
}

.field-footer p {
  margin: 4px 0;
}

.field-footer b {
  color: var(--field-lime);
}

@media (max-width: 1000px) {
  .field-platform,
  .field-calculator,
  .field-contact {
    grid-template-columns: 1fr;
  }

  .field-proof {
    grid-template-columns: repeat(2, 1fr);
  }

  .field-proof > div:nth-child(2) {
    border-right: 0;
  }

  .field-proof > div:nth-child(-n + 2) {
    border-bottom: 1px solid #334033;
  }
}

@media (max-width: 700px) {
  .field-nav {
    grid-template-columns: 1fr auto;
    min-height: 72px;
    padding-inline: 18px;
  }

  .field-nav nav {
    display: none;
  }

  .field-nav__cta {
    padding-inline: 12px;
    font-size: 9px;
  }

  .field-hero {
    min-height: 850px;
  }

  .field-hero h1 {
    font-size: clamp(4rem, 18vw, 6.7rem);
  }

  .field-hero__bg img {
    object-position: 58% center;
  }

  .field-hero__actions {
    align-items: stretch;
    flex-direction: column;
  }

  .field-hero__status {
    display: none;
  }

  .field-hero__telemetry {
    right: 18px;
    bottom: 20px;
    left: 18px;
    grid-template-columns: repeat(3, 1fr);
  }

  .field-hero__telemetry > div {
    min-width: 0;
    padding: 10px;
  }

  .field-hero__telemetry strong {
    font-size: 14px;
  }

  .field-proof {
    grid-template-columns: 1fr;
  }

  .field-proof > div,
  .field-proof > p,
  .field-proof > div:nth-child(2) {
    border-right: 0;
    border-bottom: 1px solid #334033;
  }

  .field-platform {
    padding-inline: 18px;
  }

  .field-map {
    min-height: 650px;
  }

  .field-map__toolbar {
    right: 12px;
    left: 12px;
  }

  .field-map__toolbar button {
    flex: 1;
    padding-inline: 8px;
    font-size: 8px;
  }

  .field-process__heading {
    grid-template-columns: 1fr;
  }

  .field-process article {
    position: relative;
    top: auto !important;
    grid-template-columns: 46px 1fr;
    gap: 24px;
    min-height: 520px;
    margin-inline: 18px;
    padding: 28px;
  }

  .field-process article svg {
    grid-column: 1;
    width: 38px;
    height: 38px;
  }

  .field-process article h3,
  .field-process article p {
    grid-column: 2;
  }

  .field-process article p {
    align-self: start;
  }

  .field-calculator__results {
    grid-template-columns: 1fr;
  }

  .field-calculator__results > div {
    min-height: 130px;
    border-right: 0;
    border-bottom: 1px solid #9fa596;
  }

  .field-calculator__results > div:last-child {
    border-bottom: 0;
  }

  .field-footer {
    grid-template-columns: 1fr;
  }

  .field-footer div:last-child {
    text-align: left;
  }
}

/* Production pass: dense mission telemetry, proof and live scanning */
.field {
  --field-bg: #07110c;
  --field-ink: #f4f7f0;
  --field-lime: #9cff72;
  --field-moss: #203126;
  --field-paper: #e9e4d7;
  --field-muted: #9aa89e;
  font-family: "Space Grotesk", sans-serif;
}

.field h1,
.field h2,
.field h3 {
  font-family: "Space Grotesk", sans-serif;
}

.field-logo,
.field-nav,
.field-hero__content > p:first-child,
.field-hero__status,
.field-hero__telemetry,
.field-proof,
.field-ticker,
.field-live,
.field-platform__intro > p:first-child,
.field-process__heading > p,
.field-process article > span,
.field-calculator__copy > p:first-child,
.field-cases span,
.field-footer {
  font-family: "IBM Plex Mono", monospace;
}

.field-pointer {
  width: 128px;
  height: 128px;
  color: var(--field-lime);
  border-radius: 2px;
  background:
    linear-gradient(90deg, transparent 49.5%, rgba(156, 255, 114, 0.5) 50%, transparent 50.5%),
    linear-gradient(transparent 49.5%, rgba(156, 255, 114, 0.5) 50%, transparent 50.5%),
    rgba(156, 255, 114, 0.035);
  box-shadow: 0 0 60px rgba(156, 255, 114, 0.13);
  mix-blend-mode: screen;
}

.field-live {
  display: grid;
  grid-template-columns: minmax(0, 1.12fr) minmax(390px, 0.88fr);
  min-height: 920px;
  border-top: 1px solid rgba(156, 255, 114, 0.18);
  border-bottom: 1px solid rgba(156, 255, 114, 0.18);
  background: #07110c;
}

.field-live__visual {
  position: relative;
  overflow: hidden;
  min-height: 760px;
  border-right: 1px solid rgba(156, 255, 114, 0.18);
}

.field-live__visual::after {
  position: absolute;
  inset: 0;
  content: "";
  background:
    linear-gradient(rgba(156, 255, 114, 0.1) 1px, transparent 1px),
    linear-gradient(90deg, rgba(156, 255, 114, 0.1) 1px, transparent 1px),
    linear-gradient(90deg, transparent 65%, rgba(7, 17, 12, 0.72));
  background-size: 64px 64px, 64px 64px, 100% 100%;
  pointer-events: none;
}

.field-live__visual > img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: saturate(0.66) sepia(0.08) contrast(1.14);
}

.field-live__scan {
  position: absolute;
  z-index: 2;
  top: 0;
  bottom: 0;
  left: -100%;
  width: 100%;
  border-right: 2px solid var(--field-lime);
  background: linear-gradient(90deg, transparent 72%, rgba(156, 255, 114, 0.16));
  box-shadow: 18px 0 44px rgba(156, 255, 114, 0.14);
  pointer-events: none;
}

.field-live__target {
  position: absolute;
  z-index: 3;
  display: grid;
  gap: 4px;
  min-width: 130px;
  padding: 12px 14px;
  color: #07110c;
  background: var(--field-lime);
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.25);
}

.field-live__target::before {
  position: absolute;
  top: 100%;
  left: 20px;
  width: 1px;
  height: 70px;
  content: "";
  background: var(--field-lime);
}

.field-live__target span {
  font-size: 9px;
  letter-spacing: 0.1em;
}

.field-live__target b {
  font-size: 22px;
}

.field-live__target--one {
  top: 24%;
  left: 18%;
}

.field-live__target--two {
  right: 16%;
  bottom: 24%;
}

.field-live__panel {
  display: grid;
  align-content: center;
  gap: clamp(28px, 4vw, 54px);
  padding: clamp(48px, 6vw, 92px);
  background:
    linear-gradient(rgba(156, 255, 114, 0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(156, 255, 114, 0.035) 1px, transparent 1px);
  background-size: 32px 32px;
}

.field-live__panel > p {
  margin: 0;
  color: var(--field-lime);
  font-size: 10px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.field-live__panel h2 {
  max-width: 12ch;
  margin: 0;
  font-size: clamp(3rem, 5vw, 6rem);
  line-height: 0.88;
  letter-spacing: -0.06em;
}

.field-live__metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  border-top: 1px solid rgba(156, 255, 114, 0.22);
  border-bottom: 1px solid rgba(156, 255, 114, 0.22);
}

.field-live__metrics article {
  display: grid;
  gap: 10px;
  padding: 18px 16px 18px 0;
  border-right: 1px solid rgba(156, 255, 114, 0.22);
}

.field-live__metrics article + article {
  padding-left: 16px;
}

.field-live__metrics article:last-child {
  border-right: 0;
}

.field-live__metrics span,
.field-live__metrics small {
  color: var(--field-muted);
  font-size: 9px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.field-live__metrics strong {
  color: var(--field-lime);
  font-size: clamp(1.6rem, 2.6vw, 3rem);
  letter-spacing: -0.04em;
}

.field-live__chart {
  display: grid;
  grid-template-columns: repeat(12, minmax(10px, 1fr));
  align-items: end;
  gap: 7px;
  height: 210px;
  padding-top: 26px;
  border-bottom: 1px solid rgba(156, 255, 114, 0.28);
  background: repeating-linear-gradient(to bottom, transparent 0 51px, rgba(156, 255, 114, 0.1) 52px);
}

.field-live__bar {
  display: grid;
  grid-template-rows: 1fr auto;
  align-items: end;
  gap: 8px;
  height: 100%;
}

.field-live__bar i {
  display: block;
  min-height: 8px;
  background: linear-gradient(180deg, var(--field-lime), rgba(156, 255, 114, 0.2));
  transform-origin: bottom;
}

.field-live__bar:nth-child(4n) i {
  background: linear-gradient(180deg, #d8c7a5, rgba(216, 199, 165, 0.18));
}

.field-live__bar span {
  color: var(--field-muted);
  font-size: 8px;
  text-align: center;
}

.field-live__legend {
  display: flex;
  align-items: center;
  gap: 22px;
  color: var(--field-muted);
  font-size: 9px;
  text-transform: uppercase;
}

.field-live__legend > span {
  display: flex;
  align-items: center;
  gap: 7px;
}

.field-live__legend i {
  display: inline-block;
  width: 12px;
  height: 12px;
  background: var(--field-lime);
}

.field-live__legend span:nth-child(2) i {
  background: #d8c7a5;
}

.field-live__legend b {
  margin-left: auto;
  color: var(--field-lime);
  font-weight: 500;
}

.field-cases {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  background: var(--field-bg);
}

.field-case {
  position: relative;
  overflow: hidden;
  min-height: 720px;
  border-right: 1px solid rgba(244, 247, 240, 0.18);
}

.field-case:last-child {
  border-right: 0;
}

.field-case::after {
  position: absolute;
  inset: 0;
  content: "";
  background: linear-gradient(180deg, rgba(7, 17, 12, 0.02) 30%, rgba(7, 17, 12, 0.94));
}

.field-case img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 900ms cubic-bezier(0.16, 1, 0.3, 1), filter 500ms ease;
}

.field-case:hover img {
  transform: scale(1.04);
  filter: saturate(1.1);
}

.field-case > div {
  position: absolute;
  z-index: 2;
  right: 0;
  bottom: 0;
  left: 0;
  display: grid;
  gap: 18px;
  padding: clamp(28px, 5vw, 72px);
}

.field-case span {
  color: var(--field-lime);
  font-size: 9px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.field-case h2 {
  max-width: 11ch;
  margin: 0;
  font-size: clamp(2.8rem, 5vw, 6rem);
  line-height: 0.88;
  letter-spacing: -0.06em;
}

.field-case p {
  max-width: 50ch;
  margin: 0;
  color: rgba(244, 247, 240, 0.66);
  line-height: 1.6;
}

@media (max-width: 1000px) {
  .field-live {
    grid-template-columns: 1fr;
  }

  .field-live__visual {
    min-height: 70vh;
    border-right: 0;
    border-bottom: 1px solid rgba(156, 255, 114, 0.18);
  }

  .field-cases {
    grid-template-columns: 1fr;
  }

  .field-case {
    border-right: 0;
    border-bottom: 1px solid rgba(244, 247, 240, 0.18);
  }
}

@media (max-width: 640px) {
  .field-live__panel {
    padding-inline: 20px;
  }

  .field-live__metrics {
    grid-template-columns: 1fr;
  }

  .field-live__metrics article,
  .field-live__metrics article + article {
    padding: 18px 0;
    border-right: 0;
    border-bottom: 1px solid rgba(156, 255, 114, 0.18);
  }

  .field-live__metrics article:last-child {
    border-bottom: 0;
  }

  .field-live__chart {
    gap: 4px;
    height: 170px;
  }

  .field-live__legend {
    flex-wrap: wrap;
  }

  .field-live__legend b {
    width: 100%;
    margin-left: 0;
  }

  .field-case {
    min-height: 620px;
  }
}

/* Final polish: gravity grid, technical depth and sensor glow */
.field {
  font-family: "Onest", sans-serif;
  overflow: clip;
}

.field h1,
.field h2,
.field h3 {
  font-family: "Onest", sans-serif;
}

.field-gravity-grid {
  z-index: 2;
  opacity: 0.8;
  mix-blend-mode: screen;
  -webkit-mask-image: radial-gradient(circle at center, #000 10%, rgba(0, 0, 0, 0.9) 48%, transparent 88%);
  mask-image: radial-gradient(circle at center, #000 10%, rgba(0, 0, 0, 0.9) 48%, transparent 88%);
}

.field-nav__cta,
.field-button {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(194, 242, 61, 0.72);
  box-shadow: 0 0 0 rgba(194, 242, 61, 0);
  transition: transform 300ms ease, background 300ms ease, box-shadow 420ms ease;
}

.field-nav__cta::after,
.field-button::after {
  position: absolute;
  inset: -30% -20%;
  pointer-events: none;
  content: "";
  background: linear-gradient(112deg, transparent 34%, rgba(255, 255, 255, 0.7) 49%, transparent 64%);
  transform: translateX(-95%);
  transition: transform 750ms cubic-bezier(0.16, 1, 0.3, 1);
}

.field-nav__cta:hover,
.field-button:hover {
  background: #d8ff69;
  box-shadow: 0 0 34px rgba(194, 242, 61, 0.32), 0 18px 45px rgba(2, 12, 4, 0.32);
}

.field-nav__cta:hover::after,
.field-button:hover::after {
  transform: translateX(95%);
}

.field-proof {
  background:
    linear-gradient(90deg, rgba(194, 242, 61, 0.025), transparent 52%),
    #101710;
}

.field-live {
  position: relative;
  overflow: hidden;
  background:
    linear-gradient(rgba(194, 242, 61, 0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(194, 242, 61, 0.035) 1px, transparent 1px),
    #0b110c;
  background-size: 52px 52px;
}

.field-live::before {
  position: absolute;
  right: -0.04em;
  bottom: -0.12em;
  pointer-events: none;
  content: "K-184";
  color: rgba(194, 242, 61, 0.025);
  font-family: "IBM Plex Mono", monospace;
  font-size: clamp(12rem, 27vw, 32rem);
  font-weight: 600;
  line-height: 0.72;
}

.field-live__panel {
  position: relative;
  z-index: 1;
  backdrop-filter: blur(2px);
}

.field-platform {
  background:
    radial-gradient(circle at 8% 20%, rgba(194, 242, 61, 0.12), transparent 26%),
    repeating-linear-gradient(135deg, transparent 0 38px, rgba(16, 23, 16, 0.035) 39px 40px),
    var(--field-paper);
}

.field-case,
.field-process article {
  --spot-color: rgba(194, 242, 61, 0.13);
  transition: transform 550ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 550ms ease, border-color 550ms ease;
}

.field-case:hover {
  z-index: 3;
  box-shadow: 0 38px 100px rgba(0, 0, 0, 0.28);
  transform: translateY(-8px);
}

.field-process article:hover {
  z-index: 2;
  border-color: rgba(194, 242, 61, 0.48);
  box-shadow: inset 0 0 55px rgba(194, 242, 61, 0.055), 0 28px 70px rgba(0, 0, 0, 0.16);
  transform: translateY(-9px);
}

.field-calculator {
  background:
    radial-gradient(circle at 88% 20%, rgba(194, 242, 61, 0.13), transparent 28%),
    linear-gradient(rgba(194, 242, 61, 0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(194, 242, 61, 0.035) 1px, transparent 1px),
    #111811;
  background-size: auto, 44px 44px, 44px 44px, auto;
}

@media (max-width: 700px) {
  .field-gravity-grid {
    opacity: 0.48;
  }
}

/* QA pass: seamless telemetry, meaningful map layers and usable controls */
.field-ticker > .field-ticker__track {
  display: flex;
  gap: 0;
  padding: 0;
  animation: field-ticker 28s linear infinite;
  will-change: transform;
}

.field-ticker__group {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 34px;
  min-width: max-content;
  padding: 18px 34px;
}

.field-ticker__group span {
  display: inline-flex;
  align-items: center;
  gap: 34px;
  white-space: nowrap;
}

.field-ticker__group i {
  font-size: 22px;
  font-style: normal;
}

.field-live__scan {
  left: 0;
  width: 38%;
  border-right-width: 1px;
  background: linear-gradient(90deg, transparent 22%, rgba(156, 255, 114, 0.05) 58%, rgba(156, 255, 114, 0.18));
  box-shadow: 16px 0 42px rgba(156, 255, 114, 0.12);
}

.field-map__layer {
  position: absolute;
  z-index: 2;
  inset: 0;
  pointer-events: none;
  animation: field-layer-in 520ms cubic-bezier(0.16, 1, 0.3, 1) both;
}

@keyframes field-layer-in {
  from {
    opacity: 0;
    transform: scale(0.985);
  }
}

.field-map__overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
}

.field-map__region {
  fill: rgba(194, 242, 61, 0.15);
  stroke: #c2f23d;
  stroke-width: 4;
  vector-effect: non-scaling-stroke;
  filter: drop-shadow(0 0 13px rgba(194, 242, 61, 0.28));
}

.field-map__region--anomaly {
  stroke-dasharray: 10 7;
}

.field-map__overlay--moisture .field-map__region {
  fill: rgba(72, 176, 234, 0.13);
  stroke: #70d8ff;
  filter: drop-shadow(0 0 12px rgba(112, 216, 255, 0.24));
}

.field-map__region--parcel {
  stroke-width: 3;
}

.field-map__region--pivot {
  fill: rgba(72, 176, 234, 0.08);
}

.field-map__irrigation {
  fill: none;
  stroke: rgba(112, 216, 255, 0.95);
  stroke-width: 4;
  stroke-dasharray: 13 9;
  vector-effect: non-scaling-stroke;
}

.field-map__route-shadow,
.field-map__route {
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
  vector-effect: non-scaling-stroke;
}

.field-map__route-shadow {
  stroke: rgba(7, 17, 12, 0.72);
  stroke-width: 15;
}

.field-map__route {
  stroke: var(--field-lime);
  stroke-width: 5;
  stroke-dasharray: 14 10;
  filter: drop-shadow(0 0 9px rgba(194, 242, 61, 0.58));
  animation: field-route-flow 1.4s linear infinite;
}

@keyframes field-route-flow {
  to {
    stroke-dashoffset: -24;
  }
}

.field-map__tag rect {
  fill: var(--field-lime);
  filter: drop-shadow(0 7px 18px rgba(7, 17, 12, 0.34));
}

.field-map__overlay--moisture .field-map__tag rect {
  fill: #70d8ff;
}

.field-map__tag text {
  fill: #07110c;
  font-family: "IBM Plex Mono", monospace;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.05em;
}

.field-map__marker {
  transform-box: fill-box;
  transform-origin: center;
}

.field-map__marker circle:first-child {
  fill: #07110c;
  stroke: var(--field-lime);
  stroke-width: 7;
  vector-effect: non-scaling-stroke;
  filter: drop-shadow(0 0 8px rgba(194, 242, 61, 0.52));
}

.field-map__marker circle:last-child {
  fill: var(--field-lime);
}

.field-map__overlay--moisture .field-map__marker circle:first-child {
  stroke: #70d8ff;
}

.field-map__overlay--moisture .field-map__marker circle:last-child {
  fill: #70d8ff;
}

.field-map__zone {
  position: absolute;
  min-width: 120px;
  min-height: 52px;
  border: 2px solid var(--field-lime);
  background: rgba(194, 242, 61, 0.13);
  box-shadow: inset 0 0 38px rgba(194, 242, 61, 0.08), 0 0 28px rgba(194, 242, 61, 0.08);
  transform: translate(-50%, -50%) rotate(-2deg);
}

.field-map__zone b {
  position: absolute;
  top: 10px;
  left: 10px;
  padding: 6px 8px;
  color: #07110c;
  background: var(--field-lime);
  font-family: "IBM Plex Mono", monospace;
  font-size: 9px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
}

.field-map--moisture .field-map__zone {
  border-color: #70d8ff;
  border-radius: 50%;
  background: rgba(52, 166, 255, 0.18);
  box-shadow: inset 0 0 44px rgba(78, 190, 255, 0.14);
}

.field-map--moisture .field-map__zone b {
  color: #071018;
  background: #70d8ff;
}

.field-map--moisture .field-map__pin {
  background: #70d8ff;
  box-shadow: 0 0 0 2px #70d8ff;
}

.field-map--route .field-map__zone {
  min-height: 24px;
  border-style: dashed;
  border-radius: 999px;
  background: rgba(194, 242, 61, 0.07);
  transform: translate(-50%, -50%) rotate(-18deg);
}

.field-map--route .field-map__zone:nth-child(2) {
  transform: translate(-50%, -50%) rotate(29deg);
}

.field-map--route .field-map__zone b {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.field-process {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1px;
  padding: 0 clamp(22px, 5vw, 84px) clamp(90px, 10vw, 150px);
}

.field-process__heading {
  grid-column: 1 / -1;
  padding-inline: 0;
  padding-bottom: 58px;
}

.field-process__progress {
  display: grid;
  grid-template-columns: auto minmax(90px, 1fr) auto;
  gap: 16px;
  align-items: center;
  margin-top: 30px;
  color: var(--field-muted);
  font-family: "IBM Plex Mono", monospace;
  font-size: 9px;
  letter-spacing: 0.08em;
}

.field-process__progress b {
  position: relative;
  display: block;
  height: 1px;
  overflow: hidden;
  background: rgba(194, 242, 61, 0.18);
}

.field-process__progress i {
  position: absolute;
  inset: 0;
  display: block;
  background: var(--field-lime);
  box-shadow: 0 0 18px rgba(194, 242, 61, 0.36);
  transform: scaleX(0);
  transform-origin: left;
}

.field-process article,
.field-process article:nth-of-type(2),
.field-process article:nth-of-type(3),
.field-process article:nth-of-type(4) {
  position: relative;
  top: auto;
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto auto auto 1fr auto;
  align-content: start;
  gap: 22px 30px;
  min-height: 430px;
  margin: 0;
  padding: clamp(30px, 4vw, 56px);
  color: var(--field-ink);
  background: #111b13;
  border-color: #405040;
  box-shadow: none;
}

.field-process article:nth-of-type(2),
.field-process article:nth-of-type(3) {
  background: #17251a;
}

.field-process article:nth-of-type(4) {
  color: #182018;
  background: #d5dcc4;
}

.field-process article > span {
  grid-column: 1;
  position: relative;
}

.field-process article > span::after {
  position: absolute;
  z-index: -1;
  top: -0.45em;
  left: -0.18em;
  color: rgba(194, 242, 61, 0.045);
  font-family: "Onest", sans-serif;
  font-size: clamp(8rem, 15vw, 15rem);
  font-weight: 800;
  line-height: 0.75;
  letter-spacing: -0.09em;
  content: "01";
}

.field-process article:nth-of-type(2) > span::after { content: "02"; }
.field-process article:nth-of-type(3) > span::after { content: "03"; color: rgba(24, 32, 24, 0.055); }
.field-process article:nth-of-type(4) > span::after { content: "04"; color: rgba(24, 32, 24, 0.075); }

.field-process article svg {
  grid-column: 1;
  grid-row: 2 / span 2;
  width: 48px;
  height: 48px;
}

.field-process article h3,
.field-process article p,
.field-process article ul,
.field-process__status {
  grid-column: 2;
}

.field-process article h3 {
  font-size: clamp(2rem, 3.4vw, 4rem);
  line-height: 0.98;
}

.field-process article p {
  max-width: 44ch;
  font-size: 16px;
  line-height: 1.6;
}

.field-process article ul {
  display: grid;
  gap: 10px;
  align-self: end;
  margin: 8px 0 0;
  padding: 0;
  color: var(--field-muted);
  font-family: "IBM Plex Mono", monospace;
  font-size: 10px;
  letter-spacing: 0.06em;
  list-style: none;
  text-transform: uppercase;
}

.field-process article li::before {
  margin-right: 10px;
  color: var(--field-lime);
  content: "↳";
}

.field-process__status {
  align-self: end;
  width: max-content;
  max-width: 100%;
  padding: 10px 12px;
  color: #07110c;
  background: var(--field-lime);
  font-family: "IBM Plex Mono", monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.field-process article:nth-of-type(4) p,
.field-process article:nth-of-type(4) ul {
  color: #4e5b49;
}

.field-process article:nth-of-type(3) p,
.field-process article:nth-of-type(3) ul {
  color: var(--field-muted);
}

.field-process article:nth-of-type(3) svg {
  color: var(--field-lime);
}

@media (min-width: 901px) {
  .field-process {
    display: block;
    padding: 0 0 clamp(84px, 11vh, 140px);
  }

  .field-process__heading {
    display: grid;
    grid-template-columns: 0.33fr 0.67fr;
    gap: 50px;
    padding: clamp(86px, 10vw, 150px) clamp(22px, 5vw, 84px) 64px;
  }

  .field-process__progress {
    grid-column: 2;
    margin-top: 10px;
  }

  .field-process article,
  .field-process article:nth-of-type(2),
  .field-process article:nth-of-type(3),
  .field-process article:nth-of-type(4) {
    position: sticky;
    display: grid;
    grid-template-columns: 54px 72px minmax(230px, 0.82fr) minmax(280px, 1fr);
    grid-template-rows: auto 1fr auto;
    gap: 30px 34px;
    align-items: start;
    min-height: clamp(440px, 52vh, 560px);
    margin: 0 clamp(22px, 5vw, 84px);
    padding: clamp(34px, 4.5vw, 68px);
    border-color: #405040;
    box-shadow: 0 -24px 70px rgba(3, 8, 3, 0.32);
  }

  .field-process article:nth-of-type(1) {
    --field-stack: 1;
    z-index: 1;
    top: 82px;
    color: var(--field-ink);
    background: #111b13;
  }

  .field-process article:nth-of-type(2) {
    --field-stack: 2;
    z-index: 2;
    top: 98px;
    color: var(--field-ink);
    background: #17251a;
  }

  .field-process article:nth-of-type(3) {
    --field-stack: 3;
    z-index: 3;
    top: 114px;
    color: #182018;
    background: #d5dcc4;
  }

  .field-process article:nth-of-type(4) {
    --field-stack: 4;
    z-index: 4;
    top: 130px;
    color: #182018;
    background: var(--field-lime);
  }

  .field-process article:hover {
    z-index: var(--field-stack);
    border-color: rgba(194, 242, 61, 0.52);
    box-shadow: inset 0 0 55px rgba(194, 242, 61, 0.055), 0 -24px 70px rgba(3, 8, 3, 0.32);
    transform: none;
  }

  .field-process article > span {
    grid-column: 1;
    grid-row: 1;
  }

  .field-process article svg {
    grid-column: 2;
    grid-row: 1 / span 2;
    width: 54px;
    height: 54px;
  }

  .field-process article h3 {
    grid-column: 3;
    grid-row: 1;
    font-size: clamp(2.7rem, 4.25vw, 5.6rem);
  }

  .field-process article p {
    grid-column: 4;
    grid-row: 1;
    max-width: 46ch;
    font-size: 17px;
  }

  .field-process article ul {
    grid-column: 3;
    grid-row: 2;
    align-self: end;
    margin: 0;
  }

  .field-process__status {
    grid-column: 4;
    grid-row: 2;
    align-self: end;
  }

  .field-process article:nth-of-type(3) svg,
  .field-process article:nth-of-type(4) svg {
    color: #26351f;
  }

  .field-process article:nth-of-type(3) p,
  .field-process article:nth-of-type(3) ul,
  .field-process article:nth-of-type(4) p,
  .field-process article:nth-of-type(4) ul {
    color: #4e5b49;
  }
}

.field-calculator {
  color: #182018;
  background:
    radial-gradient(circle at 88% 20%, rgba(86, 126, 45, 0.13), transparent 28%),
    linear-gradient(rgba(35, 62, 31, 0.075) 1px, transparent 1px),
    linear-gradient(90deg, rgba(35, 62, 31, 0.075) 1px, transparent 1px),
    #e5e9dc;
  background-size: auto, 44px 44px, 44px 44px, auto;
}

.field-calculator__controls {
  padding: clamp(24px, 3vw, 42px);
  background: rgba(248, 250, 242, 0.72);
  border: 1px solid rgba(38, 60, 31, 0.28);
  backdrop-filter: blur(12px);
}

.field-calculator input[type="range"] {
  width: 100%;
  min-height: 38px;
  cursor: pointer;
  appearance: none;
  background: transparent;
}

.field-calculator input[type="range"]::-webkit-slider-runnable-track {
  height: 12px;
  background: #bac8aa;
  border: 1px solid #617151;
}

.field-calculator input[type="range"]::-webkit-slider-thumb {
  width: 32px;
  height: 32px;
  margin-top: -11px;
  appearance: none;
  background: var(--field-lime);
  border: 4px solid #182018;
  border-radius: 50%;
  box-shadow: 0 0 0 5px rgba(194, 242, 61, 0.24);
}

.field-calculator input[type="range"]::-moz-range-track {
  height: 12px;
  background: #bac8aa;
  border: 1px solid #617151;
}

.field-calculator input[type="range"]::-moz-range-thumb {
  width: 26px;
  height: 26px;
  background: var(--field-lime);
  border: 4px solid #182018;
  border-radius: 50%;
}

.field-calculator__results {
  background: rgba(255, 255, 255, 0.46);
  border-color: #7e8c70;
}

.field-calculator__results > div {
  min-width: 0;
  border-color: #9aa88d;
}

.field-calculator__results span,
.field-calculator__controls small {
  color: #4e5c49;
}

.field-calculator__results strong {
  min-width: 0;
  font-size: clamp(1.4rem, 2.3vw, 2.9rem);
  font-variant-numeric: tabular-nums;
  line-height: 0.95;
  white-space: nowrap;
}

.field-calculator__results strong > span {
  display: inline-block;
  max-width: none;
  color: inherit;
  font: inherit;
  letter-spacing: inherit;
  line-height: inherit;
  white-space: nowrap;
}

.field-live__metrics strong span {
  max-width: none;
  color: inherit;
  font: inherit;
  letter-spacing: inherit;
  line-height: inherit;
  text-transform: none;
}

@media (max-width: 900px) {
  .field-process {
    grid-template-columns: 1fr;
    padding-inline: 18px;
  }

  .field-process article,
  .field-process article:nth-of-type(2),
  .field-process article:nth-of-type(3),
  .field-process article:nth-of-type(4) {
    min-height: 360px;
    margin-inline: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .field-ticker > .field-ticker__track {
    animation: none;
  }
}
